import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Filter, X } from "lucide-react";
import { cn } from "@/lib/utils";

export type SubscriptionTransactionFilterState = {
  searchTerm: string;
  statuses: string[];
  transactionTypes: string[];
  paymentTypes: string[];
  paymentMethods: string[];
  tenantIds: string[];
  dateFrom: string;
  dateTo: string;
  expFrom: string;
  expTo: string;
  amountMin: string;
  amountMax: string;
  hasCoupon: boolean | null;
};

type SubscriptionTransactionFiltersProps = {
  filters: SubscriptionTransactionFilterState;
  onFiltersChange: (filters: SubscriptionTransactionFilterState) => void;
  statuses: string[];
  transactionTypes: string[];
  paymentTypes: string[];
  paymentMethods: string[];
  tenants: Array<{ id: string; label: string }>;
  counts?: {
    statuses: Record<string, number>;
    transactionTypes: Record<string, number>;
    paymentTypes: Record<string, number>;
    paymentMethods: Record<string, number>;
    tenants: Record<string, number>;
    coupons: { com: number; sem: number };
  };
};

type TabType =
  | "resumo"
  | "status"
  | "tipo-transacao"
  | "tipo-pagamento"
  | "metodo"
  | "clientes_saas"
  | "periodo_pagamento"
  | "periodo_expiracao"
  | "valor"
  | "cupom";

export function SubscriptionTransactionFilters({
  filters,
  onFiltersChange,
  statuses,
  transactionTypes,
  paymentTypes,
  paymentMethods,
  tenants,
  counts,
}: SubscriptionTransactionFiltersProps) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>("resumo");
  const [tenantSearch, setTenantSearch] = useState("");

  const filterCount =
    filters.statuses.length +
    filters.transactionTypes.length +
    filters.paymentTypes.length +
    filters.paymentMethods.length +
    filters.tenantIds.length +
    (filters.dateFrom ? 1 : 0) +
    (filters.dateTo ? 1 : 0) +
    (filters.expFrom ? 1 : 0) +
    (filters.expTo ? 1 : 0) +
    (filters.amountMin ? 1 : 0) +
    (filters.amountMax ? 1 : 0) +
    (filters.hasCoupon !== null ? 1 : 0);

  const clearAllFilters = () => {
    onFiltersChange({
      searchTerm: filters.searchTerm,
      statuses: [],
      transactionTypes: [],
      paymentTypes: [],
      paymentMethods: [],
      tenantIds: [],
      dateFrom: "",
      dateTo: "",
      expFrom: "",
      expTo: "",
      amountMin: "",
      amountMax: "",
      hasCoupon: null,
    });
  };

  const removeArrayItem = (key: keyof Pick<SubscriptionTransactionFilterState, "statuses" | "transactionTypes" | "paymentTypes" | "paymentMethods" | "tenantIds">, value?: string) => {
    const current = (filters as any)[key] as string[];
    onFiltersChange({ ...filters, [key]: value ? current.filter((v) => v !== value) : [] });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <TooltipProvider>
        <Tooltip open={!open ? undefined : false}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="outline" className="relative">
                <Filter className="h-4 w-4 mr-2" />
                Filtros
                {filterCount > 0 && (
                  <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center" variant="default">
                    {filterCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Filtre a listagem de transações</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className="w-[560px] p-0" align="start">
        <div className="flex h-[460px]">
          <div className="w-56 border-r bg-muted/30 p-2 overflow-y-auto">
            {[
              { id: "resumo", label: "Resumo", icon: "📋" },
              { id: "status", label: "Status", icon: "📊" },
              { id: "tipo-transacao", label: "Tipo de Transação", icon: "🔄" },
              { id: "tipo-pagamento", label: "Tipo de Pagamento", icon: "🏷️" },
              { id: "metodo", label: "Método", icon: "💳" },
              { id: "clientes_saas", label: "Clientes SaaS", icon: "🏢" },
              { id: "periodo_pagamento", label: "Período Pagamento", icon: "📅" },
              { id: "periodo_expiracao", label: "Período Expiração", icon: "⏳" },
              { id: "valor", label: "Valor", icon: "💰" },
              { id: "cupom", label: "Cupom", icon: "🎟️" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={cn(
                  "w-full flex items-center justify-between px-3 py-2 rounded text-sm mb-1",
                  activeTab === (tab.id as TabType) ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                )}
              >
                <span className="flex items-center gap-2">
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </span>
                <Badge variant={activeTab === (tab.id as TabType) ? "secondary" : "default"} className="h-5 px-2 text-[11px]">
                  {tab.id === "status" && (filters.statuses.length || 0)}
                  {tab.id === "tipo-transacao" && (filters.transactionTypes.length || 0)}
                  {tab.id === "tipo-pagamento" && (filters.paymentTypes.length || 0)}
                  {tab.id === "metodo" && (filters.paymentMethods.length || 0)}
                  {tab.id === "clientes_saas" && (filters.tenantIds.length || 0)}
                  {tab.id === "periodo_pagamento" && ((filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0))}
                  {tab.id === "periodo_expiracao" && ((filters.expFrom ? 1 : 0) + (filters.expTo ? 1 : 0))}
                  {tab.id === "valor" && ((filters.amountMin ? 1 : 0) + (filters.amountMax ? 1 : 0))}
                  {tab.id === "cupom" && (filters.hasCoupon !== null ? 1 : 0)}
                </Badge>
              </button>
            ))}
          </div>

          <div className="flex-1 p-4 overflow-y-auto">
            {activeTab === "resumo" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Resumo dos Filtros</h3>
                  {filterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                      Limpar tudo
                    </Button>
                  )}
                </div>
                {filterCount === 0 ? (
                  <p className="text-muted-foreground text-sm">Nenhum filtro aplicado</p>
                ) : (
                  <div className="space-y-3">
                    {filters.statuses.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Status</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.statuses.map((s) => (
                            <Badge key={s} variant="secondary" className="gap-1 capitalize">
                              {s}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeArrayItem("statuses", s)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {filters.transactionTypes.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Tipo de Transação</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.transactionTypes.map((t) => (
                            <Badge key={t} variant="secondary" className="gap-1">
                              {t}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeArrayItem("transactionTypes", t)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {filters.paymentTypes.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Tipo de Pagamento</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.paymentTypes.map((t) => (
                            <Badge key={t} variant="secondary" className="gap-1">
                              {t}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeArrayItem("paymentTypes", t)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {filters.paymentMethods.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Métodos</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.paymentMethods.map((m) => (
                            <Badge key={m} variant="secondary" className="gap-1">
                              {m}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => removeArrayItem("paymentMethods", m)} />
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {filters.tenantIds.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Clientes SaaS</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {filters.tenantIds.map((tid) => {
                            const t = tenants.find((x) => x.id === tid);
                            return (
                              <Badge key={tid} variant="secondary" className="gap-1">
                                {t?.label || tid}
                                <X className="h-3 w-3 cursor-pointer" onClick={() => removeArrayItem("tenantIds", tid)} />
                              </Badge>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(filters.dateFrom || filters.dateTo) && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Período de Pagamento</Label>
                        <div className="flex flex-wrap gap-2 mt-1 items-center">
                          {filters.dateFrom && (
                            <Badge variant="secondary" className="gap-1">
                              De {filters.dateFrom}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => onFiltersChange({ ...filters, dateFrom: "" })} />
                            </Badge>
                          )}
                          {filters.dateTo && (
                            <Badge variant="secondary" className="gap-1">
                              Até {filters.dateTo}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => onFiltersChange({ ...filters, dateTo: "" })} />
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    {(filters.expFrom || filters.expTo) && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Período de Expiração</Label>
                        <div className="flex flex-wrap gap-2 mt-1 items-center">
                          {filters.expFrom && (
                            <Badge variant="secondary" className="gap-1">
                              De {filters.expFrom}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => onFiltersChange({ ...filters, expFrom: "" })} />
                            </Badge>
                          )}
                          {filters.expTo && (
                            <Badge variant="secondary" className="gap-1">
                              Até {filters.expTo}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => onFiltersChange({ ...filters, expTo: "" })} />
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    {(filters.amountMin || filters.amountMax) && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Valor</Label>
                        <div className="flex flex-wrap gap-2 mt-1 items-center">
                          {filters.amountMin && (
                            <Badge variant="secondary" className="gap-1">
                              Mín {filters.amountMin}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => onFiltersChange({ ...filters, amountMin: "" })} />
                            </Badge>
                          )}
                          {filters.amountMax && (
                            <Badge variant="secondary" className="gap-1">
                              Máx {filters.amountMax}
                              <X className="h-3 w-3 cursor-pointer" onClick={() => onFiltersChange({ ...filters, amountMax: "" })} />
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    {filters.hasCoupon !== null && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Cupom</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          <Badge variant="secondary" className="gap-1">
                            {filters.hasCoupon ? "Com cupom" : "Sem cupom"}
                            <X className="h-3 w-3 cursor-pointer" onClick={() => onFiltersChange({ ...filters, hasCoupon: null })} />
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === "status" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Status</h3>
                <div className="space-y-2">
                  {statuses.map((status) => (
                    <div key={status} className="flex items-center space-x-2">
                      <Checkbox
                        id={`status-${status}`}
                        checked={filters.statuses.includes(status)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            statuses: checked ? [...filters.statuses, status] : filters.statuses.filter((s) => s !== status),
                          });
                        }}
                      />
                      <Label htmlFor={`status-${status}`} className="capitalize flex-1 flex items-center gap-2">
                        {status}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.statuses?.[status] ?? 0}</Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "tipo-transacao" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Tipo de Transação</h3>
                <div className="space-y-2">
                  {transactionTypes.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`tt-${type}`}
                        checked={filters.transactionTypes.includes(type)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            transactionTypes: checked ? [...filters.transactionTypes, type] : filters.transactionTypes.filter((t) => t !== type),
                          });
                        }}
                      />
                      <Label htmlFor={`tt-${type}`} className="flex-1 flex items-center gap-2">
                        {type}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.transactionTypes?.[type] ?? 0}</Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "tipo-pagamento" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Tipo de Pagamento</h3>
                <div className="space-y-2">
                  {paymentTypes.map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`pt-${type}`}
                        checked={filters.paymentTypes.includes(type)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            paymentTypes: checked ? [...filters.paymentTypes, type] : filters.paymentTypes.filter((t) => t !== type),
                          });
                        }}
                      />
                      <Label htmlFor={`pt-${type}`} className="flex-1 flex items-center gap-2">
                        {type}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.paymentTypes?.[type] ?? 0}</Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "metodo" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Métodos de Pagamento</h3>
                <div className="space-y-2">
                  {paymentMethods.map((method) => (
                    <div key={method} className="flex items-center space-x-2">
                      <Checkbox
                        id={`pm-${method}`}
                        checked={filters.paymentMethods.includes(method)}
                        onCheckedChange={(checked) => {
                          onFiltersChange({
                            ...filters,
                            paymentMethods: checked ? [...filters.paymentMethods, method] : filters.paymentMethods.filter((m) => m !== method),
                          });
                        }}
                      />
                      <Label htmlFor={`pm-${method}`} className="flex-1 flex items-center gap-2">
                        {method}
                        <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.paymentMethods?.[method] ?? 0}</Badge>
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "clientes_saas" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Clientes SaaS</h3>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Buscar clientes SaaS..."
                    value={tenantSearch}
                    onChange={(e) => setTenantSearch(e.target.value)}
                    className="w-full border rounded px-2 py-2 text-sm"
                  />
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {tenants
                      .filter((t) => t.label.toLowerCase().includes(tenantSearch.toLowerCase()))
                      .map((t) => (
                        <div key={t.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`tenant-${t.id}`}
                            checked={filters.tenantIds.includes(t.id)}
                            onCheckedChange={(checked) => {
                              onFiltersChange({
                                ...filters,
                                tenantIds: checked ? [...filters.tenantIds, t.id] : filters.tenantIds.filter((x) => x !== t.id),
                              });
                            }}
                          />
                          <Label htmlFor={`tenant-${t.id}`} className="flex-1 flex items-center gap-2">
                            {t.label}
                            <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.tenants?.[t.id] ?? 0}</Badge>
                          </Label>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "periodo_pagamento" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Período de Pagamento</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pay-from" className="text-sm font-medium mb-2 block">De</Label>
                    <input id="pay-from" type="date" value={filters.dateFrom} onChange={(e) => onFiltersChange({ ...filters, dateFrom: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" />
                  </div>
                  <div>
                    <Label htmlFor="pay-to" className="text-sm font-medium mb-2 block">Até</Label>
                    <input id="pay-to" type="date" value={filters.dateTo} onChange={(e) => onFiltersChange({ ...filters, dateTo: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "periodo_expiracao" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Período de Expiração</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="exp-from" className="text-sm font-medium mb-2 block">De</Label>
                    <input id="exp-from" type="date" value={filters.expFrom} onChange={(e) => onFiltersChange({ ...filters, expFrom: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" />
                  </div>
                  <div>
                    <Label htmlFor="exp-to" className="text-sm font-medium mb-2 block">Até</Label>
                    <input id="exp-to" type="date" value={filters.expTo} onChange={(e) => onFiltersChange({ ...filters, expTo: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "valor" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Valor</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount-min" className="text-sm font-medium mb-2 block">Mínimo</Label>
                    <input id="amount-min" type="number" value={filters.amountMin} onChange={(e) => onFiltersChange({ ...filters, amountMin: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" />
                  </div>
                  <div>
                    <Label htmlFor="amount-max" className="text-sm font-medium mb-2 block">Máximo</Label>
                    <input id="amount-max" type="number" value={filters.amountMax} onChange={(e) => onFiltersChange({ ...filters, amountMax: e.target.value })} className="w-full border rounded px-2 py-1 text-sm" />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "cupom" && (
              <div className="space-y-4">
                <h3 className="font-semibold">Cupom</h3>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cupom-com"
                      checked={filters.hasCoupon === true}
                      onCheckedChange={(checked) => onFiltersChange({ ...filters, hasCoupon: checked ? true : null })}
                    />
                    <Label htmlFor="cupom-com" className="flex-1 flex items-center gap-2">
                      Com cupom
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.coupons?.com ?? 0}</Badge>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cupom-sem"
                      checked={filters.hasCoupon === false}
                      onCheckedChange={(checked) => onFiltersChange({ ...filters, hasCoupon: checked ? false : null })}
                    />
                    <Label htmlFor="cupom-sem" className="flex-1 flex items-center gap-2">
                      Sem cupom
                      <Badge variant="secondary" className="h-5 px-1 text-[12px]">{counts?.coupons?.sem ?? 0}</Badge>
                    </Label>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

